import { condoApi } from './api';
import { Customer, CustomerType, CustomerApiResponse } from '../types/customer';
import { logger } from './logger';

export const customerService = {
  // Fetch all customers from all endpoints
  getAllCustomers: async (): Promise<CustomerApiResponse> => {
    try {
      // For now, we'll try the unified endpoint first, fallback to individual endpoints
      try {
        const response = await condoApi.getCustomers();
        return response.data;
      } catch (error) {
        // Fallback: fetch from individual endpoints and combine
        logger.debug('customer.service', 'Unified customers endpoint not available, using individual endpoints');

        const [condosResponse, factoriesResponse, companiesResponse] = await Promise.allSettled([
          condoApi.getCondos(),
          condoApi.getFactories(),
          condoApi.getCompanies()
        ]);

        const customers: Customer[] = [];

        // Process condos
        if (condosResponse.status === 'fulfilled' && condosResponse.value?.data?.data) {
          const condos = condosResponse.value.data.data.map((condo: any) => ({
            ...condo,
            type: 'condominio' as const,
            condoType: condo.type || 'apartamento'
          }));
          customers.push(...condos);
        }

        // Process factories (may not exist yet)
        if (factoriesResponse.status === 'fulfilled' && factoriesResponse.value?.data?.data) {
          const factories = factoriesResponse.value.data.data.map((factory: any) => ({
            ...factory,
            type: 'fabrica' as const
          }));
          customers.push(...factories);
        }

        // Process companies (may not exist yet)
        if (companiesResponse.status === 'fulfilled' && companiesResponse.value?.data?.data) {
          const companies = companiesResponse.value.data.data.map((company: any) => ({
            ...company,
            type: 'empresa' as const
          }));
          customers.push(...companies);
        }

        return {
          data: customers,
          total: customers.length
        };
      }
    } catch (error) {
      logger.error('customer.service', 'Failed to fetch customers', error);
      throw error;
    }
  },

  // Fetch customers by type
  getCustomersByType: async (type: CustomerType): Promise<CustomerApiResponse> => {
    try {
      let response;
      switch (type) {
        case 'condominio':
          response = await condoApi.getCondos();
          return {
            data: response.data.data?.map((item: any) => ({
              ...item,
              type: 'condominio' as const,
              condoType: item.type || 'apartamento'
            })) || [],
            total: response.data.data?.length || 0
          };
        case 'fabrica':
          response = await condoApi.getFactories();
          return {
            data: response.data.data?.map((item: any) => ({
              ...item,
              type: 'fabrica' as const
            })) || [],
            total: response.data.data?.length || 0
          };
        case 'empresa':
          response = await condoApi.getCompanies();
          return {
            data: response.data.data?.map((item: any) => ({
              ...item,
              type: 'empresa' as const
            })) || [],
            total: response.data.data?.length || 0
          };
        default:
          throw new Error(`Unknown customer type: ${type}`);
      }
    } catch (error) {
      logger.error('customer.service', `Failed to fetch ${type}s`, error, { type });
      // Return empty result instead of throwing for graceful fallback
      return { data: [], total: 0 };
    }
  },

  // Get customer by ID (tries all types)
  getCustomerById: async (id: string): Promise<Customer | null> => {
    try {
      // Try unified endpoint first
      try {
        const response = await condoApi.getCustomer(id);
        return response.data;
      } catch {
        // Fallback: try individual endpoints
        const promises = [
          condoApi.getCondo(id).then(res => ({ ...res.data, type: 'condominio' as const })),
          condoApi.getFactory(id).then(res => ({ ...res.data, type: 'fabrica' as const })),
          condoApi.getCompany(id).then(res => ({ ...res.data, type: 'empresa' as const }))
        ];

        const results = await Promise.allSettled(promises);
        const successResult = results.find(result => result.status === 'fulfilled');

        return successResult && successResult.status === 'fulfilled' ? successResult.value : null;
      }
    } catch (error) {
      logger.error('customer.service', 'Failed to fetch customer by ID', error, { id });
      return null;
    }
  },

  // Create customer
  createCustomer: async (customerData: Omit<Customer, 'id'>): Promise<Customer> => {
    try {
      // Try unified endpoint first
      try {
        const response = await condoApi.createCustomer(customerData);
        return response.data;
      } catch {
        // Fallback: use type-specific endpoints
        let response;
        switch (customerData.type) {
          case 'condominio':
            response = await condoApi.createCondo(customerData);
            return { ...response.data, type: 'condominio' as const };
          case 'fabrica':
            response = await condoApi.createFactory(customerData);
            return { ...response.data, type: 'fabrica' as const };
          case 'empresa':
            response = await condoApi.createCompany(customerData);
            return { ...response.data, type: 'empresa' as const };
          default:
            throw new Error(`Unknown customer type: ${customerData.type}`);
        }
      }
    } catch (error) {
      logger.error('customer.service', 'Failed to create customer', error, {
        type: customerData.type
      });
      throw error;
    }
  },

  // Update customer
  updateCustomer: async (id: string, customerData: Partial<Customer>): Promise<Customer> => {
    try {
      // Try unified endpoint first
      try {
        const response = await condoApi.updateCustomer(id, customerData);
        return response.data;
      } catch {
        // Fallback: try type-specific endpoints
        // First, get the customer to determine its type
        const customer = await customerService.getCustomerById(id);
        if (!customer) {
          throw new Error('Customer not found');
        }

        let response;
        switch (customer.type) {
          case 'condominio':
            response = await condoApi.updateCondo(id, customerData);
            return { ...response.data, type: 'condominio' as const };
          case 'fabrica':
            response = await condoApi.updateFactory(id, customerData);
            return { ...response.data, type: 'fabrica' as const };
          case 'empresa':
            response = await condoApi.updateCompany(id, customerData);
            return { ...response.data, type: 'empresa' as const };
          default:
            throw new Error(`Unknown customer type: ${(customer as any).type}`);
        }
      }
    } catch (error) {
      logger.error('customer.service', 'Failed to update customer', error, { id });
      throw error;
    }
  },

  /**
   * Disables a customer. Customers are never deleted — the endpoint behind this only sets
   * is_active = false, and deletion is blocked at the ORM and database layers (54 ON DELETE
   * CASCADE foreign keys reference customers(id), so a real delete would erase the whole
   * condomínio). Reverse it with setCustomerActive(id, true).
   *
   * The previous implementation fell back to deleteCondo/deleteFactory/deleteCompany when the
   * unified endpoint failed. Those operate on the separate `condos` table, not on `customers`,
   * so the fallback left the customer active while soft-deleting an unrelated record — a silent
   * inconsistency. Removed: if disabling fails, it fails.
   */
  disableCustomer: async (id: string): Promise<void> => {
    try {
      await condoApi.deleteCustomer(id);
    } catch (error) {
      logger.error('customer.service', 'Failed to disable customer', error, { id });
      throw error;
    }
  },

  /** Enables or disables a customer. The undo for disableCustomer. */
  setCustomerActive: async (id: string, isActive: boolean): Promise<void> => {
    try {
      await condoApi.setCustomerActive(id, isActive);
    } catch (error) {
      logger.error('customer.service', 'Failed to change customer active status', error, { id, isActive });
      throw error;
    }
  },

  /** Lists disabled customers so they can be found and reactivated. */
  getInactiveCustomers: async (): Promise<Customer[]> => {
    const res = await condoApi.getInactiveCustomers();
    return res.data?.data ?? [];
  },

  // ============================================================================
  // DEPARTMENT MANAGEMENT (Empresa)
  // ============================================================================

  // Get departments for a customer
  getDepartments: async (customerId: string) => {
    try {
      const response = await condoApi.getDepartments(customerId);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to fetch departments', error, { customerId });
      throw error;
    }
  },

  // Create department
  createDepartment: async (customerId: string, department: any) => {
    try {
      const response = await condoApi.createDepartment(customerId, department);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to create department', error, { customerId });
      throw error;
    }
  },

  // Update department
  updateDepartment: async (customerId: string, departmentId: string, updates: any) => {
    try {
      const response = await condoApi.updateDepartment(customerId, departmentId, updates);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to update department', error, { customerId, departmentId });
      throw error;
    }
  },

  // Delete department
  deleteDepartment: async (customerId: string, departmentId: string) => {
    try {
      await condoApi.deleteDepartment(customerId, departmentId);
    } catch (error) {
      logger.error('customer.service', 'Failed to delete department', error, { customerId, departmentId });
      throw error;
    }
  },

  // Get department members
  getDepartmentMembers: async (customerId: string, departmentId: string) => {
    try {
      const response = await condoApi.getDepartmentMembers(customerId, departmentId);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to fetch department members', error, { customerId, departmentId });
      throw error;
    }
  },

  // Add member to department
  addDepartmentMember: async (customerId: string, departmentId: string, personId: string) => {
    try {
      const response = await condoApi.addDepartmentMember(customerId, departmentId, personId);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to add department member', error, { customerId, departmentId, personId });
      throw error;
    }
  },

  // Remove member from department
  removeDepartmentMember: async (customerId: string, departmentId: string, personId: string) => {
    try {
      const response = await condoApi.removeDepartmentMember(customerId, departmentId, personId);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to remove department member', error, { customerId, departmentId, personId });
      throw error;
    }
  },

  // ============================================================================
  // EMPLOYEE MANAGEMENT (Empresa)
  // ============================================================================

  // Get employees for a customer
  getEmployees: async (customerId: string, params?: {
    department_id?: string;
    department_ids?: string;
    search?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    authorization_status?: 'authorized' | 'pending';
  }) => {
    try {
      const response = await condoApi.getEmployees(customerId, params);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to fetch employees', error, { customerId });
      throw error;
    }
  },

  // Create employee
  createEmployee: async (customerId: string, employee: any) => {
    try {
      const response = await condoApi.createEmployee(customerId, employee);
      return response.data;
    } catch (error) {
      logger.error('customer.service', 'Failed to create employee', error, { customerId });
      throw error;
    }
  },

  // Assign employee to department
  assignEmployeeToDepartment: async (employeeId: string, departmentId: string) => {
    try {
      await condoApi.assignEmployeeToDepartment(employeeId, departmentId);
    } catch (error) {
      logger.error('customer.service', 'Failed to assign employee to department', error, { employeeId, departmentId });
      throw error;
    }
  }
};

export default customerService;